import React from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';

import { QueryClientProvider, QueryClient, useMutation } from 'react-query';
import NotificationManager, { useNotificationManager } from 'common/compounds/NotificationManager';
import { SuiNotification } from '@splashthat/sui';
import * as Dialog from '@radix-ui/react-dialog';
import X from '@splashthat/doppler/icons/X';

import { getDoNotSellApiClient } from 'lib/SplashApiClient/subclients/DoNotSellApiClient';
import { useBootstrap } from 'utils/bootstrap/useBootstrap';
import Logger from 'observability/Logger';
import { useGuestAccessHashStandalone } from 'utils/hooks/useGuestAccessHashStandalone';

import DoNotSellBanner, { StyledButton } from './DoNotSellBanner';
import {
    BANNER_COLOR_LIGHT,
    BANNER_COLOR_DARK,
    GUEST_INFORMATION,
    GUEST_ACCESS_HASH,
    GPC_DO_NOT_SELL_STORAGE_KEY_PREFIX,
    DONS_CONSENT_SUBMIT_ERROR_MESSAGE,
} from '../constants';

const overlayShow = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const contentShow = keyframes`
    from {
        opacity: 0;
        transform: translate(-50%, -48%) scale(0.96);
    }
    to {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
    }
`;

/* eslint-disable @splashthat/sui-colors-only */
const DialogOverlay = styled(Dialog.Overlay)`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 9999;
    animation: ${overlayShow} 200ms cubic-bezier(0.16, 1, 0.3, 1);
`;

const DialogContentWrapper = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 10000;
    transform: translate(-50%, -50%);
    animation: ${contentShow} 200ms cubic-bezier(0.16, 1, 0.3, 1);
`;

const IconButton = styled(StyledButton)`
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;

    position: absolute;
    top: 1rem;
    right: 1.5rem;
`;

const LOG = new Logger('GuestDoNotSellModal');
const queryClient = new QueryClient();

const GuestDoNotSellModalContent = ({ trigger, open = false, onOpenChange, ...props }) => {
    const titleId = React.useId();
    const descriptionId = React.useId();
    const { showNotification } = useNotificationManager();

    const {
        event: { id: eventId },
    } = useBootstrap(['event']);

    const { guestAccessHash } = useGuestAccessHashStandalone(`dons/${eventId}/guestAccessHash`);

    React.useEffect(() => {
        const handleGPCDetection = async () => {
            if (!guestAccessHash) return;

            const gpcSignal = navigator.globalPrivacyControl;
            if (!gpcSignal) return;

            const localStorageKey = `${GPC_DO_NOT_SELL_STORAGE_KEY_PREFIX}${guestAccessHash}`;
            if (localStorage.getItem(localStorageKey)) return;

            try {
                await getDoNotSellApiClient().submitEventDoNotSellConsent({
                    eventId,
                    payload: {
                        type: GUEST_ACCESS_HASH,
                        guestAccessHash,
                    },
                });

                localStorage.setItem(localStorageKey, 'true');
                LOG.info('Do not sell consent recorded successfully through GPC', { guestAccessHash, eventId });
            } catch (error) {
                LOG.error('Failed to record Do not sell consent through GPC', error);
            }
        };

        handleGPCDetection();
    }, [guestAccessHash, eventId]);

    const { mutate: submitConsent, isLoading: isSubmitting } = useMutation(
        (values) =>
            getDoNotSellApiClient().submitEventDoNotSellConsent({
                eventId,
                payload: { type: GUEST_INFORMATION, ...values },
            }),
        {
            onSuccess: () => {
                showNotification({
                    id: window?.crypto?.randomUUID(),
                    message: props?.confirmationMessage || 'Thank you, your request has been submitted.',
                    variation: SuiNotification.SUCCESS,
                });
                onOpenChange(false);
            },
            onError: (err) => {
                LOG.error('Error submitting do not sell consent', err);
                const errorMessage =
                    err?.status === 409 && typeof err?.data?.message === 'string'
                        ? err.data.message.trim()
                        : DONS_CONSENT_SUBMIT_ERROR_MESSAGE;
                showNotification({
                    id: window?.crypto?.randomUUID(),
                    message: errorMessage,
                    variation: SuiNotification.ERROR,
                });
            },
        },
    );

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
            <Dialog.Portal>
                <DialogOverlay />
                <Dialog.Content asChild aria-labelledby={titleId} aria-describedby={descriptionId}>
                    <DialogContentWrapper>
                        <DoNotSellBanner
                            {...props}
                            appearance={props?.bannerDefaultColor}
                            isPreview={false}
                            isSubmitting={isSubmitting}
                            onCancel={() => onOpenChange(false)}
                            onConfirm={(values) => submitConsent(values)}
                            titleId={titleId}
                            descriptionId={descriptionId}
                        />
                        <Dialog.Close asChild>
                            <IconButton type="button" aria-label="Close" disabled={isSubmitting}>
                                <X />
                            </IconButton>
                        </Dialog.Close>
                    </DialogContentWrapper>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

GuestDoNotSellModalContent.propTypes = {
    trigger: PropTypes.node.isRequired,
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    bannerDefaultColor: PropTypes.oneOf([BANNER_COLOR_LIGHT, BANNER_COLOR_DARK]),
};

GuestDoNotSellModalContent.defaultProps = {
    open: false,
    onOpenChange: () => {},
    bannerDefaultColor: BANNER_COLOR_LIGHT,
};

const GuestDoNotSellModal = (props) => (
    <QueryClientProvider client={queryClient}>
        <NotificationManager>
            <GuestDoNotSellModalContent {...props} />
        </NotificationManager>
    </QueryClientProvider>
);

GuestDoNotSellModal.propTypes = GuestDoNotSellModalContent.propTypes;
GuestDoNotSellModal.defaultProps = GuestDoNotSellModalContent.defaultProps;

export default GuestDoNotSellModal;
